import { ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ListTooltipContent } from '@/components/atoms/ListTooltipContent/ListTooltipContent';
import { type ConnectedDialog } from '@/hooks/useDialogs';
import { useIsTruncated } from '@/hooks/useIsTruncated';

const DIALOG_NAME_CLASSES = 'min-w-0 truncate text-sm leading-5 font-normal';

interface AssignedToCellProps {
  brainId: string;
  slug: string;
  dialogs: ConnectedDialog[];
}

export const AssignedToCell = ({ brainId, slug, dialogs }: AssignedToCellProps) => {
  const { t } = useTranslation();
  const [first, ...rest] = dialogs;
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [first?.label]);

  const dialogHref = (dialogId: string) =>
    `/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialogId}`;

  if (dialogs.length === 0) {
    return <span className="text-foreground">-</span>;
  }

  if (rest.length === 0) {
    return (
      <InfoTooltip
        content={isTruncated ? first.label : t('webhook.go_to_dialog')}
        size={isTruncated ? 'auto' : 'default'}
      >
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(dialogHref(first.url), '_blank', 'noopener,noreferrer');
          }}
          className="group/assigned flex max-w-full min-w-0 cursor-pointer items-center gap-1 text-left text-foreground hover:text-informational"
        >
          <span ref={ref} className={DIALOG_NAME_CLASSES}>
            {first.label}
          </span>
          <ExternalLinkIcon className="h-4 w-4 shrink-0 opacity-0 transition-opacity group-hover/assigned:opacity-100" />
        </button>
      </InfoTooltip>
    );
  }

  return (
    <InfoTooltip
      variant="rich"
      size="auto"
      side="right"
      align="center"
      content={
        <ListTooltipContent
          title={t('webhook.assigned_to_tooltip')}
          items={dialogs.map((dialog) => ({
            id: dialog.url,
            label: dialog.label,
            href: dialogHref(dialog.url),
          }))}
        />
      }
    >
      <button
        type="button"
        onClick={(e) => e.preventDefault()}
        className="flex max-w-full min-w-0 cursor-pointer items-center gap-1 text-left text-foreground"
      >
        <span className={DIALOG_NAME_CLASSES}>{first.label}</span>
        <span className="shrink-0 text-sm leading-5">+{rest.length}</span>
      </button>
    </InfoTooltip>
  );
};
